import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { fetchDailyProgress } from '@/services/streaksApi';
import { DailyProgress } from '@/types/streaks';
import { format, subDays, differenceInDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { CalendarIcon, Users, BookOpen, TrendingUp, Award } from "lucide-react";
import { cn } from "@/lib/utils";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

type ComparisonType = 'week' | 'month' | 'custom';

const DailyProgressChart: React.FC = () => {
    const [currentWeekData, setCurrentWeekData] = useState<DailyProgress[]>([]);
    const [previousWeekData, setPreviousWeekData] = useState<DailyProgress[]>([]);
    const [loading, setLoading] = useState(true);
    const [startDate, setStartDate] = useState<Date>(subDays(new Date(), 7));
    const [endDate, setEndDate] = useState<Date>(new Date());
    const [isStartDateOpen, setIsStartDateOpen] = useState(false);
    const [isEndDateOpen, setIsEndDateOpen] = useState(false);
    const [comparisonType, setComparisonType] = useState<ComparisonType>('week');
    const [periodMetrics, setPeriodMetrics] = useState<{
        totalReads: number;
        activeReaders: number;
        averageStreak: number;
        topStreak: number;
    }>({
        totalReads: 0,
        activeReaders: 0,
        averageStreak: 0,
        topStreak: 0
    });

    const handleComparisonTypeChange = (type: ComparisonType) => {
        setComparisonType(type);
        const today = new Date();

        switch (type) {
            case 'week':
                const currentWeekStart = startOfWeek(today, { weekStartsOn: 1 });
                const currentWeekEnd = endOfWeek(today, { weekStartsOn: 1 });
                setStartDate(currentWeekStart);
                setEndDate(currentWeekEnd);
                break;
            case 'month':
                const currentMonthStart = startOfMonth(today);
                const currentMonthEnd = endOfMonth(today);
                setStartDate(currentMonthStart);
                setEndDate(currentMonthEnd);
                break;
            case 'custom':
                // Mantém as datas atuais
                break;
        }
    };

    const fetchData = async (start: Date, end: Date) => {
        setLoading(true);
        let previousStart: Date;
        let previousEnd: Date;

        if (comparisonType === 'week') {
            previousStart = subWeeks(start, 1);
            previousEnd = subWeeks(end, 1);
        } else if (comparisonType === 'month') {
            previousStart = subMonths(start, 1);
            previousEnd = subMonths(end, 1);
        } else {
            const daysDiff = differenceInDays(end, start);
            previousStart = subDays(start, daysDiff + 1);
            previousEnd = subDays(start, 1);
        }

        try {
            const [currentPeriod, previousPeriod] = await Promise.all([
                fetchDailyProgress(
                    format(start, 'yyyy-MM-dd'),
                    format(end, 'yyyy-MM-dd')
                ),
                fetchDailyProgress(
                    format(previousStart, 'yyyy-MM-dd'),
                    format(previousEnd, 'yyyy-MM-dd')
                )
            ]);

            setCurrentWeekData(currentPeriod.dailyProgress);
            setPreviousWeekData(previousPeriod.dailyProgress);
            setPeriodMetrics({
                totalReads: currentPeriod.totalReads,
                activeReaders: currentPeriod.activeReaders,
                averageStreak: currentPeriod.averageStreak,
                topStreak: currentPeriod.topStreak
            });
        } catch (error) {
            console.error('Erro ao buscar dados:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(startDate, endDate);
    }, [startDate, endDate]);

    const handleStartDateSelect = (date: Date | undefined) => {
        if (date) {
            setStartDate(date);
            setIsStartDateOpen(false);
            setComparisonType('custom');
        }
    };

    const handleEndDateSelect = (date: Date | undefined) => {
        if (date) {
            setEndDate(date);
            setIsEndDateOpen(false);
            setComparisonType('custom');
        }
    };

    // Geração das labels: sempre usa as datas do período atual
    const labels = currentWeekData.map(item => format(new Date(item.date), 'dd/MM', { locale: ptBR }));

    const chartData = {
        labels,
        datasets: [
            {
                label: 'Leitores Ativos (Atual)',
                data: currentWeekData.map(item => item.activeReaders),
                borderColor: '#FF3366',
                backgroundColor: 'rgba(255, 51, 102, 0.1)',
                tension: 0.1,
                yAxisID: 'y1',
            },
            {
                label: 'Leitores Ativos (Anterior)',
                data: previousWeekData.map(item => item.activeReaders),
                borderColor: '#FF3366',
                backgroundColor: 'rgba(255, 51, 102, 0.1)',
                borderDash: [6, 6],
                tension: 0.1,
                yAxisID: 'y1',
            },
            {
                label: 'Média de Streak (Atual)',
                data: currentWeekData.map(item => item.averageStreak),
                borderColor: '#222',
                backgroundColor: 'rgba(34, 34, 34, 0.1)',
                tension: 0.1,
                yAxisID: 'y2',
            },
            {
                label: 'Média de Streak (Anterior)',
                data: previousWeekData.map(item => item.averageStreak),
                borderColor: '#888',
                backgroundColor: 'rgba(136, 136, 136, 0.1)',
                borderDash: [6, 6],
                tension: 0.1,
                yAxisID: 'y2',
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        interaction: {
            mode: 'index' as const,
            intersect: false,
        },
        plugins: {
            legend: {
                position: 'top' as const,
                labels: {
                    color: '#666',
                    font: {
                        family: "'Inter', sans-serif",
                    },
                },
            },
            tooltip: {
                callbacks: {
                    label: function(context: any) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.dataset.yAxisID === 'y2') {
                            label += context.parsed.y.toFixed(2) + ' dias';
                        } else {
                            label += context.parsed.y.toLocaleString('pt-BR');
                        }
                        return label;
                    }
                }
            }
        },
        scales: {
            y: {
                type: 'linear' as const,
                display: true,
                position: 'left' as const,
                title: {
                    display: true,
                    text: 'Leituras',
                    color: '#666',
                },
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)',
                },
                ticks: {
                    color: '#666',
                    font: {
                        family: "'Inter', sans-serif",
                    },
                },
            },
            y1: {
                type: 'linear' as const,
                display: true,
                position: 'right' as const,
                title: {
                    display: true,
                    text: 'Leitores',
                    color: '#666',
                },
                grid: {
                    drawOnChartArea: false,
                },
                ticks: {
                    color: '#666',
                    font: {
                        family: "'Inter', sans-serif",
                    },
                },
            },
            y2: {
                type: 'linear' as const,
                display: true,
                position: 'right' as const,
                title: {
                    display: true,
                    text: 'Streak',
                    color: '#666',
                },
                grid: {
                    drawOnChartArea: false,
                },
                ticks: {
                    color: '#666',
                    font: {
                        family: "'Inter', sans-serif",
                    },
                },
            },
            x: {
                grid: {
                    color: 'rgba(0, 0, 0, 0.1)',
                },
                ticks: {
                    color: '#666',
                    font: {
                        family: "'Inter', sans-serif",
                    },
                },
            },
        },
    };

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center space-x-4">
                            <BookOpen className="h-10 w-10 text-pink-500" />
                            <div>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Total de Leituras</p>
                                <h3 className="text-2xl font-bold dark:text-white">
                                    {periodMetrics.totalReads.toLocaleString('pt-BR')}
                                </h3>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center space-x-4">
                            <Users className="h-10 w-10 text-pink-500" />
                            <div>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Leitores Ativos</p>
                                <h3 className="text-2xl font-bold dark:text-white">
                                    {periodMetrics.activeReaders.toLocaleString('pt-BR')}
                                </h3>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center space-x-4">
                            <TrendingUp className="h-10 w-10 text-pink-500" />
                            <div>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Média de Streak</p>
                                <h3 className="text-2xl font-bold dark:text-white">
                                    {periodMetrics.averageStreak.toFixed(2)} dias
                                </h3>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-6">
                        <div className="flex items-center space-x-4">
                            <Award className="h-10 w-10 text-pink-500" />
                            <div>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Maior Streak</p>
                                <h3 className="text-2xl font-bold dark:text-white">
                                    {periodMetrics.topStreak} dias
                                </h3>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
            <Card>
                <CardHeader>
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <CardTitle className="dark:text-white">Progresso Diário</CardTitle>
                        <div className="flex flex-wrap gap-2">
                            <div className="flex gap-2">
                                <Button
                                    variant={comparisonType === 'week' ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => handleComparisonTypeChange('week')}
                                    className="text-xs"
                                >
                                    Semana Atual vs Anterior
                                </Button>
                                <Button
                                    variant={comparisonType === 'month' ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => handleComparisonTypeChange('month')}
                                    className="text-xs"
                                >
                                    Mês Atual vs Anterior
                                </Button>
                                <Button
                                    variant={comparisonType === 'custom' ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => handleComparisonTypeChange('custom')}
                                    className="text-xs"
                                >
                                    Período Personalizado
                                </Button>
                            </div>
                            {comparisonType === 'custom' && (
                                <div className="flex gap-2">
                                    <Popover open={isStartDateOpen} onOpenChange={setIsStartDateOpen}>
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className={cn(
                                                    "w-[120px] justify-start text-left font-normal",
                                                    !startDate && "text-muted-foreground"
                                                )}
                                            >
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {startDate ? format(startDate, "dd/MM/yyyy") : "Início"}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                            <Calendar
                                                mode="single"
                                                selected={startDate}
                                                onSelect={handleStartDateSelect}
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>

                                    <Popover open={isEndDateOpen} onOpenChange={setIsEndDateOpen}>
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className={cn(
                                                    "w-[120px] justify-start text-left font-normal",
                                                    !endDate && "text-muted-foreground"
                                                )}
                                            >
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {endDate ? format(endDate, "dd/MM/yyyy") : "Fim"}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                            <Calendar
                                                mode="single"
                                                selected={endDate}
                                                onSelect={handleEndDateSelect}
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>
                                </div>
                            )}
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="flex items-center justify-center h-[400px]">
                            <div className="w-8 h-8 border-4 border-t-[#FF3366] rounded-full animate-spin" />
                        </div>
                    ) : (
                        <Line data={chartData} options={chartOptions} />
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default DailyProgressChart; 